import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { ProductSelector, type ProductSelectorRef, type SelectedProduct } from '@shared/components/ui/ProductSelector';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

// Definir interfaz Product localmente para evitar problemas de importación
interface Product {
  id: string | number;
  name: string;
  code?: string;
  image?: string;
}

interface ProductModalProps {
  /** Lista de productos disponibles para seleccionar */
  products: Product[];
  /** Función que se ejecuta cuando se seleccionan productos */
  onProductsSelected?: (selectedProducts: SelectedProduct[]) => void;
  /** Productos preseleccionados (opcional) */
  initialSelectedProducts?: SelectedProduct[];
  /** Si está cargando datos */
  isLoading?: boolean;
  /** Texto del botón principal */
  buttonText?: string;
  /** Texto del botón cuando está cargando */
  loadingText?: string;
  /** Si el botón está deshabilitado */
  disabled?: boolean;
  /** Si se permite selección múltiple */
  allowMultiple?: boolean;
  /** Cantidad máxima por producto */
  maxQuantity?: number;
  /** Título de la modal */
  title?: string;
}

interface ProductModalRef {
  /** Abrir el selector de productos */
  openSelector: () => void;
  /** Cerrar el selector de productos */
  closeSelector: () => void;
  /** Limpiar selección actual */
  clearSelection: () => void;
}

const ProductSelectorModal = React.memo(
  forwardRef<ProductModalRef, ProductModalProps>(
    (
      {
        products = [],
        onProductsSelected,
        initialSelectedProducts = [],
        isLoading = false,
        buttonText = 'Seleccionar productos',
        loadingText = 'Cargando productos...',
        disabled = false,
        allowMultiple = true,
        maxQuantity = 999,
        title = 'Seleccionar productos',
      },
      ref,
    ) => {
      const productSelectorRef = useRef<ProductSelectorRef>(null);

      // Exponer métodos del ref al componente padre
      useImperativeHandle(ref, () => ({
        openSelector: () => {
          productSelectorRef.current?.open();
        },
        closeSelector: () => {
          productSelectorRef.current?.close();
        },
        clearSelection: () => {
          productSelectorRef.current?.clearSelection();
        },
      }));

      // Handler para cuando se seleccionan productos (onSubmit del ProductSelector)
      const handleSubmit = useCallback(
        (selectedProducts: SelectedProduct[]) => {
          onProductsSelected?.(selectedProducts);
          productSelectorRef.current?.close();
        },
        [onProductsSelected],
      );

      // Memoizar props del ProductSelector
      const productSelectorProps = useMemo(
        () => ({
          ref: productSelectorRef,
          products,
          onSubmit: handleSubmit,
          initialSelectedProducts,
          isLoading,
          disabled,
          allowMultiple,
          maxQuantity,
          title,
        }),
        [products, handleSubmit, initialSelectedProducts, isLoading, disabled, allowMultiple, maxQuantity, title],
      );

      return (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => productSelectorRef.current?.open()}
            disabled={disabled || isLoading}
            data-testid="open-product-selector-button"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            {isLoading ? loadingText : buttonText}
          </Button>
          <ProductSelector {...productSelectorProps} />
        </>
      );
    },
  ),
);

ProductSelectorModal.displayName = 'ProductSelectorModal';

export { ProductSelectorModal };
export default ProductSelectorModal;
